import { useState, useEffect, useMemo, useCallback, useRef, startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import type { DateRange } from '@/components/ui';
import { apiHelpers, extractApiData } from '@/api/core';
import { useConnectionStore } from '@/store/connectionStore';
import { useAppStore } from '@/store/loadingStore';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { getSensorTypeInfo } from '@packages/types/device/lite/lite_constraints';
import { SENSOR_TYPE_MAP, SensorType } from '@packages/types/device/lite/sensors';
import { MINI_SENSOR_TYPE_MAP } from '@packages/types/device/mini/sensors';
import { getMiniSensorInfo } from '@packages/types/device/mini/mini_constraints';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import type { ErrorMessage as ErrorMessageShape } from '@/utils/errors/types';
import { parseDatalogApiError } from '@/utils/errors';
import type { GasReadingData, SensorData, DatalogDevice } from './datalogTabTypes';

function parseColumnarResponse(
  response: { columns: string[]; data: any[][] },
  deviceType: DeviceType,
  _serialNumber: string
): GasReadingData[] {
  const { columns, data } = response;
  if (!data || data.length === 0) return [];

  if (deviceType === DeviceType.MINI) {
    return data.map(row => ({
      timestamp: row[0],
      temperature: row[1],
      battery_volt: row[2],
      lel_sensor_volt: null,
      system_status: 0,
      system_status_name: row[3],
      gas_conc: row[4],
      stel_conc: row[5],
      twa_conc: row[6],
    }));
  }

  const sensorCount = (columns.length - 5) / 5;
  return data.map(row => {
    const sensors: any[] = [];
    for (let i = 0; i < sensorCount; i++) {
      const baseIdx = 5 + i * 5;
      const sensorCol = columns[baseIdx];
      const match = sensorCol.match(/^(.+)\((.+)\)$/);
      sensors.push({
        sensor_name: match ? match[1] : sensorCol,
        gas_conc: row[baseIdx],
        stel_conc: row[baseIdx + 1],
        twa_conc: row[baseIdx + 2],
        sensor_status_name: row[baseIdx + 3],
        decimal: row[baseIdx + 4] ?? 0,
        unit_name: match ? match[2] : 'PPM',
      });
    }
    return { timestamp: row[0], temperature: row[1], battery_volt: row[2], lel_sensor_volt: row[3], system_status: 0, system_status_name: row[4], sensors };
  });
}

interface UseGasReadingsParams {
  device: DatalogDevice;
  deviceType: DeviceType;
  dateRange: DateRange;
}

export function useGasReadings({ device, deviceType, dateRange }: UseGasReadingsParams) {
  const { t } = useTranslation('datalog');
  const { showLoading, hideLoading } = useAppStore();
  const [gasReadings, setGasReadings] = useState<GasReadingData[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<ErrorMessageShape | null>(null);
  const isDownloading = useConnectionStore((state: any) => state.isDownloading);

  const serialNumber = useMemo(() => device.serial_number, [device.serial_number]);

  useEffect(() => {
    let cancelled = false;
    let showedGlobalLoading = false;

    const fetchGasReadings = async () => {
      if (isDownloading) return;
      if (!dateRange.from || !dateRange.to) {
        setGasReadings([]);
        return;
      }

      setLoadingData(true);
      showLoading(t(i18nKeys.datalog.dataLoading));
      showedGlobalLoading = true;
      setError(null);
      try {
        const allData: GasReadingData[] = [];
        const pageSize = 1000;
        const maxRecords = 50000;
        let offset = 0;
        let hasMore = true;

        while (!cancelled && hasMore && allData.length < maxRecords) {
          const endpoint =
            deviceType === DeviceType.MINI
              ? 'miniLogs.gasReadingsByDevice'
              : 'liteLogs.gasReadingsByDevice';

          const response = await apiHelpers.get<any>(endpoint, undefined, {
            serial_number: serialNumber,
            start_date: dateRange.from,
            end_date: dateRange.to,
            limit: pageSize,
            offset,
            filter_unchanged: true,
          });

          if (cancelled) {
            return;
          }

          const responseData = extractApiData(response);

          let parsedData: GasReadingData[] = [];
          if (responseData.columns && responseData.data) {
            parsedData = parseColumnarResponse(responseData, deviceType, serialNumber);
          } else {
            parsedData = responseData;
          }

          if (parsedData.length === 0) {
            hasMore = false;
            break;
          }

          allData.push(...parsedData);

          offset += pageSize;
          if (parsedData.length < pageSize) {
            hasMore = false;
          }
        }

        if (cancelled) {
          return;
        }

        startTransition(() => {
          setGasReadings(allData);
        });
      } catch (err: unknown) {
        if (!cancelled) {
          setError(parseDatalogApiError(err));
          setGasReadings([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
        if (showedGlobalLoading) {
          hideLoading();
          showedGlobalLoading = false;
        }
      }
    };

    void fetchGasReadings();

    return () => {
      cancelled = true;
      setLoadingData(false);
      if (showedGlobalLoading) {
        hideLoading();
        showedGlobalLoading = false;
      }
    };
  }, [
    serialNumber,
    dateRange.from,
    dateRange.to,
    deviceType,
    isDownloading,
    t,
    showLoading,
    hideLoading,
  ]);

  // Lite: 센서별 데이터 추출
  const sensorDataMap = useMemo(() => {
    if (deviceType !== DeviceType.LITE || gasReadings.length === 0) {
      return new Map<string, SensorData[]>();
    }

    const map = new Map<string, SensorData[]>();
    gasReadings.forEach(reading => {
      reading.sensors?.forEach(sensor => {
        if (!map.has(sensor.sensor_name)) {
          map.set(sensor.sensor_name, []);
        }
        map.get(sensor.sensor_name)!.push({
          timestamp: reading.timestamp,
          temperature: reading.temperature,
          battery_volt: reading.battery_volt,
          lel_sensor_volt: reading.lel_sensor_volt,
          system_status: reading.system_status,
          system_status_name: reading.system_status_name,
          gas_conc: sensor.gas_conc,
          stel_conc: sensor.stel_conc,
          twa_conc: sensor.twa_conc,
          unit_name: sensor.unit_name,
          sensor_name: sensor.sensor_name,
          decimal: sensor.decimal ?? 0,
          sensor_status: sensor.sensor_status,
          sensor_status_name: sensor.sensor_status_name,
        });
      });
    });
    return map;
  }, [gasReadings, deviceType]);

  // Mini: has_twa_stel
  const miniHasTwaStel = useMemo(() => {
    if (deviceType !== DeviceType.MINI) return false;
    if (!device.sensor_types || device.sensor_types.length === 0) return true;
    const sensorTypeEnum = MINI_SENSOR_TYPE_MAP[device.sensor_types[0]];
    if (sensorTypeEnum === undefined) return true;
    return getMiniSensorInfo(sensorTypeEnum)?.has_twa_stel ?? true;
  }, [deviceType, device.sensor_types]);

  // Lite: 선택된 센서의 has_twa_stel
  const getLiteHasTwaStel = (selectedSensor: string | null): boolean => {
    if (deviceType !== DeviceType.LITE || !selectedSensor) return false;

    let sensorTypeEnum = SENSOR_TYPE_MAP[selectedSensor];
    if (sensorTypeEnum === undefined) {
      for (const value of Object.values(SENSOR_TYPE_MAP)) {
        const sensorInfo = getSensorTypeInfo(value);
        if (sensorInfo.label === selectedSensor) {
          sensorTypeEnum = value;
          break;
        }
      }
    }
    if (sensorTypeEnum === undefined) return true;
    return getSensorTypeInfo(sensorTypeEnum).has_twa_stel;
  };

  // Lite: 선택된 센서가 LEL 타입인지 확인
  const LEL_SENSOR_TYPES = [SensorType.LEL_LPC, SensorType.LEL_IR, SensorType.LEL_1Series];

  const getIsLelSensor = (selectedSensor: string | null): boolean => {
    if (deviceType !== DeviceType.LITE || !selectedSensor) return false;

    let sensorTypeEnum = SENSOR_TYPE_MAP[selectedSensor];
    if (sensorTypeEnum === undefined) {
      for (const value of Object.values(SENSOR_TYPE_MAP)) {
        const sensorInfo = getSensorTypeInfo(value);
        if (sensorInfo.label === selectedSensor) {
          sensorTypeEnum = value;
          break;
        }
      }
    }
    if (sensorTypeEnum === undefined) return false;
    return LEL_SENSOR_TYPES.includes(sensorTypeEnum);
  };

  // Lite: 센서 옵션
  const sensorOptions = useMemo(() => {
    if (deviceType !== DeviceType.LITE || sensorDataMap.size === 0) return [];
    return Array.from(sensorDataMap.keys()).map(name => ({ label: name, value: name }));
  }, [sensorDataMap, deviceType]);

  // 엑셀용 전체 데이터 fetch (filter_unchanged: false, 캡 없음)
  const [excelLoading, setExcelLoading] = useState(false);
  const excelAbortRef = useRef<(() => void) | undefined>(undefined);

  const fetchAllForExcel = useCallback(async (): Promise<GasReadingData[]> => {
    excelAbortRef.current?.();
    let cancelled = false;
    excelAbortRef.current = () => { cancelled = true; };

    setExcelLoading(true);
    showLoading(t(i18nKeys.datalog.dataLoading));
    try {
      const allData: GasReadingData[] = [];
      const pageSize = 5000;
      let offset = 0;
      let hasMore = true;

      while (!cancelled && hasMore) {
        const endpoint =
          deviceType === DeviceType.MINI
            ? 'miniLogs.gasReadingsByDevice'
            : 'liteLogs.gasReadingsByDevice';

        const response = await apiHelpers.get<any>(endpoint, undefined, {
          serial_number: serialNumber,
          start_date: dateRange.from,
          end_date: dateRange.to,
          limit: pageSize,
          offset,
          filter_unchanged: false,
        });

        if (cancelled) return [];

        const responseData = extractApiData(response);

        let parsedData: GasReadingData[] = [];
        if (responseData.columns && responseData.data) {
          parsedData = parseColumnarResponse(responseData, deviceType, serialNumber);
        } else {
          parsedData = responseData;
        }

        if (parsedData.length === 0) {
          hasMore = false;
          break;
        }

        allData.push(...parsedData);
        offset += pageSize;
        if (parsedData.length < pageSize) {
          hasMore = false;
        }
      }

      return cancelled ? [] : allData;
    } finally {
      setExcelLoading(false);
      hideLoading();
    }
  }, [serialNumber, dateRange.from, dateRange.to, deviceType, t, showLoading, hideLoading]);

  return {
    gasReadings,
    loadingData,
    error,
    sensorDataMap,
    sensorOptions,
    miniHasTwaStel,
    getLiteHasTwaStel,
    getIsLelSensor,
    fetchAllForExcel,
    excelLoading,
  };
}
